import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FilePlus2,
  Loader2,
  MessageSquare,
  Pencil,
  Plus,
  RefreshCw,
  ShieldCheck,
  Users,
} from 'lucide-react';
import PublicFormBuilder from './PublicFormBuilder';
import PublicFormSubmissions from './PublicFormSubmissions';
import PublicFormSmsBatch from './PublicFormSmsBatch';
import SearchBar from './SearchBar';
import { publicFormsApi, publicFormFeatureError } from '../services/publicFormsApi';
import { canSendBulkCommunications } from '../utils/bulkCommunicationAccess';
import { PUBLIC_FORMS_PATH } from '../utils/publicFormsAccess';
import {
  normalizePublicFormDraft,
  publicFormTargetLabel,
  sanitizePublicFormCaseTypes,
  sanitizePublicFormCatalog,
} from '../utils/publicFormBuilder';
import '../styles/PublicForms.css';

const parseSchema = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (error) {
    return null;
  }
};

const hydrateFormDetail = (payload, fallbackForm = null) => {
  const form = payload?.form || fallbackForm || {};
  const schemas = Array.isArray(payload?.schemas) ? payload.schemas : [];
  const currentSchemaId = Number(form.current_schema_id || form.schema_id || 0);
  const schemaRecord = schemas.find((item) => Number(item.id) === currentSchemaId)
    || schemas.find((item) => item.is_current)
    || schemas[0]
    || payload?.schema
    || null;
  const parsed = parseSchema(schemaRecord?.schema_json ?? schemaRecord?.schema ?? schemaRecord);
  return normalizePublicFormDraft({
    ...form,
    ...(parsed || {}),
    creation_settings: form.creation_settings,
    items: parsed?.items || parsed?.schema || parsed?.fields || form.items || form.fields || [],
    notification_recipients: form.notification_recipients
      || payload?.form_notification_recipients
      || payload?.notification_recipients
      || [],
  });
};

const statusLabel = (status) => {
  const normalized = String(status || 'draft').toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

const formatDate = (value) => {
  if (!value) return 'Not yet';
  const date = new Date(String(value).replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
};

const parsePublicFormsDeepLink = (search = '') => {
  const params = new URLSearchParams(search);
  const formIds = params.getAll('form_id');
  const views = params.getAll('view');
  if (views.length > 1) return { invalid: true };
  if (formIds.length === 0) {
    if (views.length === 0) return null;
    return views[0] === 'new' ? { formId: null, view: 'new', invalid: false } : { invalid: true };
  }
  const rawFormId = formIds[0];
  const view = params.get('view') || 'manage';
  if (
    formIds.length !== 1
    || !/^[1-9][0-9]{0,15}$/.test(rawFormId)
    || !['manage', 'submissions', 'sms_batch'].includes(view)
  ) {
    return { invalid: true };
  }
  const formId = Number(rawFormId);
  return Number.isSafeInteger(formId) ? { formId, view, invalid: false } : { invalid: true };
};

const publicFormsPageClassName = (navVariant) => {
  if (navVariant === 'full') return 'page-container public-forms-page ab';
  return `page-container public-forms-page compact ${navVariant === 'compact' ? 'public-forms-nav-minimized' : 'public-forms-nav-open'}`;
};

const PublicForms = ({ endpoint, publicFormBaseUrl, navVariant = 'compact', accessLevel = '', locationSearch, onNavigate }) => {
  const [view, setView] = useState('hub');
  const [forms, setForms] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [caseTypes, setCaseTypes] = useState([]);
  const [notificationRecipients, setNotificationRecipients] = useState([]);
  const [creationOptions, setCreationOptions] = useState({});
  const [capabilities, setCapabilities] = useState({});
  const [selectedForm, setSelectedForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formsLoaded, setFormsLoaded] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [confirmRevokeId, setConfirmRevokeId] = useState(null);
  const [navigationNotice, setNavigationNotice] = useState('');
  const [browserSearch, setBrowserSearch] = useState(() => (
    typeof window === 'undefined' ? '' : window.location.search
  ));
  const search = locationSearch ?? browserSearch;
  const deepLinkHandledRef = useRef(null);
  const navigationRequestRef = useRef(0);

  useEffect(() => {
    if (locationSearch !== undefined) return undefined;
    const handlePopState = () => setBrowserSearch(window.location.search);
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [locationSearch]);

  useEffect(() => () => { navigationRequestRef.current += 1; }, []);

  const pageTitle = view !== 'hub' && selectedForm?.name ? selectedForm.name : 'Public Forms';
  useEffect(() => { document.title = `${pageTitle} | CaseDB`; }, [pageTitle]);

  const writeLocation = useCallback((formId = null, nextView = 'manage', { replace = false } = {}) => {
    // Management URLs contain an authenticated form ID, never recipient credentials.
    const params = new URLSearchParams();
    if (Number.isSafeInteger(Number(formId)) && Number(formId) > 0) {
      params.set('form_id', String(Number(formId)));
      if (nextView !== 'manage') params.set('view', nextView);
    } else if (nextView === 'new') {
      params.set('view', 'new');
    }
    const query = params.toString();
    const nextSearch = query ? `?${query}` : '';
    if (deepLinkHandledRef.current === nextSearch) return;
    deepLinkHandledRef.current = nextSearch;
    if (onNavigate) {
      onNavigate(nextSearch, { replace });
    } else {
      window.history[replace ? 'replaceState' : 'pushState'](window.history.state, '', `${PUBLIC_FORMS_PATH}${nextSearch}`);
      setBrowserSearch(nextSearch);
    }
  }, [onNavigate]);

  const returnToHub = useCallback(() => {
    navigationRequestRef.current += 1;
    setSelectedForm(null);
    setView('hub');
    setWorking(false);
    setError('');
    setNavigationNotice('');
    writeLocation();
  }, [writeLocation]);

  const loadForms = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const payload = await publicFormsApi.list({ endpoint });
      setForms(Array.isArray(payload.forms) ? payload.forms : []);
      setFormsLoaded(true);
      setCapabilities(payload.capabilities || {
        admin_enabled: payload.admin_enabled,
        public_enabled: payload.public_enabled,
        creation_enabled: payload.creation_enabled,
      });
    } catch (requestError) {
      setError(publicFormFeatureError(requestError));
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    loadForms();
  }, [loadForms]);

  const loadCatalog = useCallback(async (caseTypeId = '') => {
    const requestId = navigationRequestRef.current;
    const payload = await publicFormsApi.catalog(caseTypeId, { endpoint });
    if (requestId !== navigationRequestRef.current) return payload;
    const fields = payload.fields || payload.catalog || [];
    setCatalog(sanitizePublicFormCatalog(fields));
    setCaseTypes(sanitizePublicFormCaseTypes(payload.case_types));
    setCreationOptions(payload.creation_options || {});
    setCapabilities((current) => ({ ...current, ...(payload.capabilities || {}) }));
    return payload;
  }, [endpoint]);

  const createForm = useCallback(async ({ fromLocation = false } = {}) => {
    const requestId = ++navigationRequestRef.current;
    if (!fromLocation) writeLocation(null, 'new');
    setWorking(true);
    setError('');
    setNavigationNotice('');
    setSelectedForm(null);
    setView('hub');
    try {
      const payload = await loadCatalog();
      if (requestId !== navigationRequestRef.current) return;
      setNotificationRecipients(payload.eligible_notification_recipients || payload.notification_recipients || []);
      setSelectedForm(null);
      setView('builder');
    } catch (requestError) {
      if (requestId === navigationRequestRef.current) setError(publicFormFeatureError(requestError));
    } finally {
      if (requestId === navigationRequestRef.current) setWorking(false);
    }
  }, [loadCatalog, writeLocation]);

  const editForm = useCallback(async (form, { fromLocation = false } = {}) => {
    const requestId = ++navigationRequestRef.current;
    if (!fromLocation) writeLocation(form.id);
    setWorking(true);
    setError('');
    setNavigationNotice('');
    setSelectedForm(null);
    setView('hub');
    try {
      const [detail, catalogPayload] = await Promise.all([
        publicFormsApi.get(form.id, { endpoint }),
        loadCatalog(form.case_type_id || ''),
      ]);
      if (requestId !== navigationRequestRef.current) return;
      const hydrated = hydrateFormDetail(detail, form);
      setSelectedForm(hydrated);
      setCreationOptions(detail.creation_options || catalogPayload.creation_options || {});
      setNotificationRecipients(
        detail.eligible_notification_recipients
        || detail.notification_users
        || catalogPayload.eligible_notification_recipients
        || catalogPayload.notification_users
        || detail.notification_recipients
        || [],
      );
      setCapabilities((current) => ({ ...current, ...(detail.capabilities || {}) }));
      setView('builder');
    } catch (requestError) {
      if (requestId === navigationRequestRef.current) setError(publicFormFeatureError(requestError));
    } finally {
      if (requestId === navigationRequestRef.current) setWorking(false);
    }
  }, [endpoint, loadCatalog, writeLocation]);

  const openFormView = (form, nextView) => {
    navigationRequestRef.current += 1;
    setSelectedForm(normalizePublicFormDraft(form));
    setView(nextView);
    setWorking(false);
    setError('');
    setNavigationNotice('');
    writeLocation(form.id, nextView);
  };

  const builderNavigationId = navigationRequestRef.current;
  const handleSaved = (form) => {
    if (builderNavigationId !== navigationRequestRef.current) return;
    setSelectedForm(form);
    setForms((current) => {
      const existing = current.find((item) => Number(item.id) === Number(form.id));
      return existing
        ? current.map((item) => Number(item.id) === Number(form.id) ? { ...item, ...form } : item)
        : [...current, form];
    });
    // Persist the first saved identity without replacing the builder's local draft.
    writeLocation(form.id, 'manage', { replace: true });
  };

  useEffect(() => {
    if (loading || !formsLoaded || deepLinkHandledRef.current === search) return;
    deepLinkHandledRef.current = search;
    navigationRequestRef.current += 1;
    setSelectedForm(null);
    setView('hub');
    setWorking(false);
    setError('');
    setNavigationNotice('');
    const deepLink = parsePublicFormsDeepLink(search);
    if (!deepLink) return;
    if (deepLink.invalid) {
      setNavigationNotice('The requested public form link is invalid.');
      return;
    }
    if (deepLink.view === 'new') {
      createForm({ fromLocation: true });
      return;
    }
    const form = forms.find((candidate) => Number(candidate.id) === deepLink.formId);
    if (!form) {
      setNavigationNotice('The requested public form is unavailable or you do not have access to it.');
      return;
    }
    setNavigationNotice('');
    if (deepLink.view === 'sms_batch') {
      const available = (form.entity_mode || form.target_mode) === 'existing_case'
        && (capabilities.sms_batches?.history_available === true
          || (canSendBulkCommunications(accessLevel) && capabilities.sms_batches?.enabled === true
            && form.status === 'published' && form.requires_republish !== true && form.publication_current !== false));
      if (!available) {
        setNavigationNotice('Text batches are unavailable for this form.');
        return;
      }
    }
    if (['submissions', 'sms_batch'].includes(deepLink.view)) {
      setSelectedForm(normalizePublicFormDraft(form));
      setView(deepLink.view);
      return;
    }
    editForm(form, { fromLocation: true });
  }, [accessLevel, capabilities.sms_batches, createForm, editForm, forms, formsLoaded, loading, search]);

  const revokeForm = async (formId) => {
    if (Number(confirmRevokeId) !== Number(formId)) {
      setConfirmRevokeId(formId);
      return;
    }
    setWorking(true);
    setError('');
    try {
      await publicFormsApi.revoke(formId, { endpoint });
      setConfirmRevokeId(null);
      await loadForms();
    } catch (requestError) {
      setError(publicFormFeatureError(requestError));
    } finally {
      setWorking(false);
    }
  };

  const filteredForms = useMemo(() => forms.filter((form) => {
    const matchesStatus = statusFilter === 'all' || String(form.status || 'draft').toLowerCase() === statusFilter;
    const query = searchQuery.trim().toLowerCase();
    return matchesStatus && (!query || [form.name, form.title, publicFormTargetLabel(form.entity_mode || form.target_mode)]
      .some((value) => String(value || '').toLowerCase().includes(query)));
  }), [forms, statusFilter, searchQuery]);

  const summary = useMemo(() => ({
    total: forms.length,
    published: forms.filter((form) => form.status === 'published').length,
    submissions: forms.reduce((sum, form) => sum + Number(form.submission_count || form.submissions_count || 0), 0),
  }), [forms]);

  const pageClassName = publicFormsPageClassName(navVariant);
  const bulkSender = canSendBulkCommunications(accessLevel);

  if (view === 'sms_batch') {
    const sendEnabled = bulkSender && capabilities.sms_batches?.enabled === true && selectedForm.status === 'published'
      && selectedForm.requires_republish !== true && selectedForm.publication_current !== false;
    return <main className={pageClassName}><PublicFormSmsBatch key={selectedForm.id} form={selectedForm} endpoint={endpoint} accessLevel={accessLevel} sendEnabled={sendEnabled} onBack={returnToHub} /></main>;
  }

  if (view === 'builder') {
    return (
      <main className={`${pageClassName} public-forms-builder-page`}>
        <PublicFormBuilder
          key={builderNavigationId}
          initialForm={selectedForm}
          catalog={catalog}
          caseTypes={caseTypes}
          onCaseTypeChange={loadCatalog}
          notificationRecipients={notificationRecipients}
          creationOptions={creationOptions}
          capabilities={capabilities}
          endpoint={endpoint}
          publicFormBaseUrl={publicFormBaseUrl}
          onCancel={() => {
            returnToHub();
            loadForms();
          }}
          onSaved={handleSaved}
        />
      </main>
    );
  }

  if (view === 'submissions') {
    return (
      <main className={pageClassName}>
        <PublicFormSubmissions key={selectedForm.id} form={selectedForm} endpoint={endpoint} onBack={returnToHub} />
      </main>
    );
  }

  return (
    <main className={pageClassName}>
      <header id="page-header" className="public-forms-header">
        <div className="public-forms-header-copy">
          <h2>Public forms</h2>
          <p className="subtext">Manage forms, recipient links, and submitted entries.</p>
        </div>
        <div className="public-forms-header-actions">
          <button type="button" className="action quaternary" onClick={loadForms} disabled={loading}>
            {loading ? <Loader2 size={16} className="spinner" /> : <RefreshCw size={16} />} Refresh
          </button>
          <button type="button" className="action" onClick={createForm} disabled={working || Boolean(error)}>
            {working ? <Loader2 size={16} className="spinner" /> : <Plus size={16} />} New form
          </button>
        </div>
      </header>

      {error && (
        <div className="public-form-feature-error" role="alert">
          <ShieldCheck size={20} />
          <div><strong>Public forms are unavailable</strong><p>{error}</p></div>
        </div>
      )}

      {navigationNotice && (
        <div className="public-form-feature-error" role="alert">
          <ShieldCheck size={20} />
          <div><strong>Form unavailable</strong><p>{navigationNotice}</p></div>
        </div>
      )}

      {!error && capabilities.public_enabled === false && (
        <div className="public-form-config-note">
          <ShieldCheck size={18} />
          <p><strong>Builder available; recipient links are off.</strong> An administrator must enable the separate public runtime before a published link will open.</p>
        </div>
      )}

      <section className="public-forms-content" aria-label="Form library">
        <div className="case-filters public-forms-list-heading">
          <div className="form-group nm public-forms-library-search">
            <SearchBar expanded value={searchQuery} setSearchQuery={setSearchQuery} placeholder="Search forms" />
          </div>
          <div className="public-forms-segmented" role="group" aria-label="Filter forms by status">
            {['all', 'draft', 'published', 'revoked'].map((status) => (
              <button type="button" key={status} className={`action-tiny quaternary${statusFilter === status ? ' active' : ''}`} aria-pressed={statusFilter === status} onClick={() => setStatusFilter(status)}>
                {status === 'all' ? 'All' : statusLabel(status)}
              </button>
            ))}
          </div>
        </div>
        <section className="public-forms-summary subtext" aria-label="Public form summary">
          <span>Total forms: <strong>{summary.total}</strong></span>
          <span>Published: <strong>{summary.published}</strong></span>
          <span>Entries received: <strong>{summary.submissions}</strong></span>
        </section>

        {loading && forms.length === 0 ? (
          <div className="public-form-loading"><Loader2 className="spinner" /> Loading forms…</div>
        ) : filteredForms.length === 0 ? (
          <div className="public-form-empty-state">
            <FilePlus2 size={30} />
            <strong>{forms.length === 0 ? 'Create your first public form' : 'No forms match this filter'}</strong>
            <p>Choose writable data capture fields, set a destination, then issue a short-lived recipient link.</p>
            {forms.length === 0 && <button type="button" className="action" onClick={createForm}>Create form</button>}
          </div>
        ) : (

          <div className="public-forms-table-wrap">
            <table className="public-forms-table">
              <thead><tr><th>Form</th><th>Destination</th><th>Status</th><th>Entries</th><th>Updated</th><th><span className="sr-only">Actions</span></th></tr></thead>
              <tbody>
                {filteredForms.map((form) => (
                  <tr key={form.id}>
                    <td><button type="button" className="public-form-name-button" onClick={() => editForm(form)}><strong>{form.name || form.title || `Form ${form.id}`}</strong><small>{form.title || 'No public title'}</small></button></td>
                    <td>{publicFormTargetLabel(form.entity_mode || form.target_mode)}</td>
                    <td>
                      <span className={`public-form-status public-form-status-${form.requires_republish ? 'draft' : form.status || 'draft'}`}>
                        {form.requires_republish ? 'Needs republish' : statusLabel(form.status)}
                      </span>
                    </td>
                    <td>{Number(form.submission_count || form.submissions_count || 0)}</td>
                    <td>{formatDate(form.updated_at || form.created_at)}</td>
                    <td>
                      <div className="public-form-row-actions">
                        {(form.entity_mode || form.target_mode) === 'existing_case' && (
                          <button type="button" className="action-micro quaternary public-form-row-action public-form-row-action--view"
                            disabled={working || (capabilities.sms_batches?.history_available !== true && (!bulkSender || capabilities.sms_batches?.enabled !== true || form.status !== 'published' || form.requires_republish === true || form.publication_current === false))}
                            onClick={() => openFormView(form, 'sms_batch')}
                            aria-label={`${bulkSender ? 'Text individual links' : 'View text batch history'} for ${form.name}`}
                            title={!bulkSender ? 'Global Admin access is required for bulk texts; existing batch history remains available' : capabilities.sms_batches?.enabled === true ? 'Text individual case links' : capabilities.sms_batches?.history_available === true ? 'View previous text batches' : 'Bulk form texting is not enabled yet'}><MessageSquare size={16} aria-hidden="true" /></button>
                        )}
                        <button type="button" className="action-micro quaternary public-form-row-action public-form-row-action--view" onClick={() => openFormView(form, 'submissions')} aria-label={`View submissions for ${form.name}`} title="View submissions"><Users size={16} aria-hidden="true" /></button>
                        <button type="button" className="action-micro quaternary public-form-row-action public-form-row-action--edit" onClick={() => editForm(form)} aria-label={`Edit ${form.name}`} title="Edit form"><Pencil size={16} aria-hidden="true" /></button>
                        <button type="button" className={`action-tiny public-form-row-action public-form-row-action--revoke ${Number(confirmRevokeId) === Number(form.id) ? 'destroy' : 'quinary'}`} onClick={() => revokeForm(form.id)} disabled={working || form.status === 'revoked'}>
                          {Number(confirmRevokeId) === Number(form.id) ? 'Confirm revoke' : 'Revoke'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
};

export { hydrateFormDetail, parsePublicFormsDeepLink, publicFormsPageClassName };
export default PublicForms;
